"use client";

import { useEffect, useState } from "react";
import type { ContractReviewFeedback } from "../types";
import { deleteReviewFeedback, fetchOwnerFeedback } from "../api";
import "../contract-review.css";

interface Props {
  reviewId: string;
  documentName: string;
  onClose: () => void;
  onDeleted?: (feedbackId: string) => void;
}

/**
 * Owner view of external feedback left on a review via its shared link.
 * Newest first, grouped per clause; owner may remove any entry.
 */
export default function ReviewFeedbackDialog({ reviewId, documentName, onClose, onDeleted }: Props) {
  const [entries, setEntries] = useState<ContractReviewFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = q
    ? entries.filter(
        (f) =>
          (f.created_by_email || "").toLowerCase().includes(q) ||
          (f.clause_name || "").toLowerCase().includes(q),
      )
    : entries;

  const contributors = new Set(entries.map((x) => x.created_by_email)).size;

  useEffect(() => {
    let active = true;
    fetchOwnerFeedback(reviewId)
      .then((list) => {
        if (active) setEntries(list);
      })
      .catch((e: unknown) => {
        if (active) setError(e instanceof Error ? e.message : "Could not load feedback.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [reviewId]);

  async function remove(feedbackId: string) {
    setError(null);
    try {
      await deleteReviewFeedback(feedbackId);
      setEntries((prev) => prev.filter((x) => x.id !== feedbackId));
      onDeleted?.(feedbackId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete feedback.");
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="External feedback"
      className="cr-share-overlay"
      onClick={onClose}
    >
      <div className="cr-share-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="cr-share-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        <div className="cr-share-section">
          <div className="cr-share-title">External feedback</div>
          <div className="cr-share-sub">{documentName || "Contract review"}</div>
        </div>

        <div className="cr-share-section" style={{ flex: 1, overflowY: "auto", minHeight: 140 }}>
          {error && <div className="cr-share-error">{error}</div>}

          {loading ? (
            <p style={{ color: "var(--cr-text-soft)", fontSize: 14 }}>Loading feedback…</p>
          ) : entries.length === 0 ? (
            <div className="cr-empty" style={{ padding: "18px 8px" }}>
              <p>No external feedback yet.</p>
              <p style={{ fontSize: 13, color: "var(--cr-text-soft)", marginTop: 6 }}>
                Share the review link to collect feedback on individual clauses.
              </p>
            </div>
          ) : (
            <>
              <input
                type="search"
                className="cr-field-input"
                placeholder="Search by reviewer email or clause…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ marginBottom: 10, fontSize: 14, padding: "10px 12px" }}
              />
              <div style={{ fontSize: 12, color: "var(--cr-text-soft)", fontWeight: 600, marginBottom: 8 }}>
                {filtered.length} {filtered.length === 1 ? "entry" : "entries"} · {contributors}{" "}
                {contributors === 1 ? "contributor" : "contributors"}
              </div>
              {filtered.map((f) => (
                <div key={f.id} className="cr-feedback-row">
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 12.5, fontWeight: 650, color: "var(--cr-ink)" }}>
                      {f.created_by_email || "External reviewer"}
                    </span>
                    <span style={{ fontSize: 11.5, color: "var(--cr-text-soft)" }}>
                      {f.created_at ? new Date(f.created_at).toLocaleString() : ""}
                    </span>
                  </div>
                  {f.clause_name && (
                    <div className="cr-basis-tag cr-badge" style={{ marginTop: 8 }}>
                      {f.clause_name}
                    </div>
                  )}
                  <p style={{ margin: "8px 0 0", fontSize: 14, lineHeight: 1.5, color: "var(--cr-ink)", whiteSpace: "pre-wrap" }}>
                    {f.comment}
                  </p>
                  <button
                    type="button"
                    className="cr-fb-btn"
                    style={{ cursor: "pointer", marginTop: 8, opacity: 1, color: "var(--cr-red, #d64545)" }}
                    onClick={() => void remove(f.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {filtered.length === 0 && (
                <p style={{ color: "var(--cr-text-soft)", fontSize: 13.5, textAlign: "center", padding: "16px 8px" }}>
                  No feedback matches “{query.trim()}”.
                </p>
              )}
            </>
          )}
        </div>

        <div className="cr-share-section" style={{ paddingTop: 12 }}>
          <button type="button" className="cr-btn-secondary" style={{ width: "100%" }} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}